<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import {
  hasPushPermissions,
  requestPushPermissions,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { NOTIFICATION_TYPES, VOIP_NOTIFICATION_TYPES } from './constants';

export default {
  components: { TableHeaderCell, ToggleSwitch, CheckBox },
  data() {
    return {
      selectedEmailFlags: [],
      selectedPushFlags: [],
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      emailFlags: 'userNotificationSettings/getSelectedEmailFlags',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentRole: 'getCurrentRole',
    }),
    hasPushAPISupport() {
      return !!('Notification' in window);
    },
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    isSLAEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.SLA);
    },
    isVoipEnabled() {
      return !!(
        this.isFeatureEnabledonAccount(this.accountId, 'voip_calls') ||
        this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.VOIP_CALLS)
      );
    },
    // Filtra a lista mostrada baseado em:
    //  - role (agente nao ve adminOnly:true)
    //  - SLA enabled (se nao tem feature, esconde linhas requiresSla)
    //  - VOIP enabled (so concatena se feature ativa)
    filteredNotificationTypes() {
      const base = NOTIFICATION_TYPES.filter(n => {
        if (n.adminOnly && !this.isAdmin) return false;
        if (n.requiresSla && !this.isSLAEnabled) return false;
        return true;
      });
      const withVoip = this.isVoipEnabled
        ? base.concat(VOIP_NOTIFICATION_TYPES.filter(n => !n.adminOnly || this.isAdmin))
        : base;
      return withVoip;
    },
    // Master toggle: estado agregado das notificacoes push
    allPushEnabled() {
      return (
        this.filteredNotificationTypes.length > 0 &&
        this.filteredNotificationTypes.every(n =>
          this.selectedPushFlags.includes(`push_${n.value}`)
        )
      );
    },
  },
  watch: {
    emailFlags(value) { this.selectedEmailFlags = value; },
    pushFlags(value) { this.selectedPushFlags = value; },
  },
  mounted() {
    if (hasPushPermissions()) this.getPushSubscription();
    this.$store.dispatch('userNotificationSettings/get');
  },
  methods: {
    checkFlagStatus(type, flagType) {
      const selectedFlags = type === 'email' ? this.selectedEmailFlags : this.selectedPushFlags;
      return selectedFlags.includes(`${type}_${flagType}`);
    },
    onRegistrationSuccess() { this.hasEnabledPushPermissions = true; },
    onRequestPermissions(value) {
      if (value) {
        requestPushPermissions({ onSuccess: this.onRegistrationSuccess });
      } else {
        this.disablePushPermissions();
      }
    },
    disablePushPermissions() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription()
          .then(sub => sub ? sub.unsubscribe() : null)
          .finally(() => { this.hasEnabledPushPermissions = false; })
          .catch(() => {})
      );
    },
    getPushSubscription() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription()
          .then(sub => { this.hasEnabledPushPermissions = !!sub; })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error))
      );
    },
    async updateNotificationSettings() {
      try {
        this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedEmailFlags,
          selectedPushFlags: this.selectedPushFlags,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      }
    },
    handleInput(type, id) {
      if (type === 'email') this.handleEmailInput(id);
      else this.handlePushInput(id);
    },
    handleEmailInput(id) {
      this.selectedEmailFlags = this.toggleInput(this.selectedEmailFlags, id);
      this.updateNotificationSettings();
    },
    handlePushInput(id) {
      this.selectedPushFlags = this.toggleInput(this.selectedPushFlags, id);
      this.updateNotificationSettings();
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) return selected.filter(f => f !== current);
      return [...selected, current];
    },
    // Master toggle: liga ou desliga TODAS as notificacoes push de uma vez
    toggleAllPush(enable) {
      const allKeys = this.filteredNotificationTypes.map(n => `push_${n.value}`);
      if (enable) {
        // adiciona todas que faltam
        const next = new Set(this.selectedPushFlags);
        allKeys.forEach(k => next.add(k));
        this.selectedPushFlags = Array.from(next);
      } else {
        // remove todas
        this.selectedPushFlags = this.selectedPushFlags.filter(f => !allKeys.includes(f));
      }
      this.updateNotificationSettings();
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-4">
    <!-- Banner role/contexto -->
    <div
      v-if="!isAdmin"
      class="flex items-start gap-3 p-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30"
    >
      <span class="text-lg">ℹ</span>
      <div class="flex-1 text-xs">
        <b class="block text-sm mb-0.5 text-n-slate-12">Voce esta como agente</b>
        <span class="text-n-slate-11">
          Estamos mostrando apenas notificacoes relevantes pra agentes. Se voce e administrador desta conta, peca pra verificarem seu role.
        </span>
      </div>
    </div>

    <!-- Master toggle pra ligar/desligar todas as push -->
    <div
      class="flex items-center justify-between gap-3 p-3 rounded-xl border border-n-weak bg-n-slate-2"
    >
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-semibold text-n-slate-12">Todas as notificacoes</span>
        <span class="text-xs text-n-slate-11">
          Liga ou desliga todas as notificacoes push de uma vez
        </span>
      </div>
      <ToggleSwitch :model-value="allPushEnabled" @update:model-value="toggleAllPush" />
    </div>

    <!-- Layout desktop -->
    <div class="hidden sm:block">
      <div class="grid content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl">
        <TableHeaderCell :span="7" label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`">
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell :span="2" label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL')}`">
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell :span="3" label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH')}`">
          <span class="text-heading-3 normal-case text-n-slate-12 whitespace-nowrap">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
          </span>
        </TableHeaderCell>
      </div>
      <div v-for="(notification, index) in filteredNotificationTypes" :key="index">
        <div class="grid items-start content-center grid-cols-12 gap-4 py-3 border-b border-n-slate-3">
          <div class="flex flex-col gap-1 col-span-7 px-0 text-sm">
            <span class="text-body-main text-n-slate-12 font-medium">
              {{ notification.label.startsWith('PROFILE_') ? $t(notification.label) : notification.label }}
            </span>
            <span v-if="notification.description" class="text-xs text-n-slate-11">
              {{ notification.description }}
            </span>
          </div>
          <div
            v-for="(type, typeIndex) in ['email', 'push']"
            :key="typeIndex"
            class="flex items-start gap-2 px-0 text-sm pt-1"
            :class="`col-span-${type === 'push' ? 3 : 2}`"
          >
            <CheckBox
              :value="`${type}_${notification.value}`"
              :is-checked="checkFlagStatus(type, notification.value, selectedPushFlags)"
              @update="id => handleInput(type, id)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Layout mobile -->
    <div class="flex flex-col gap-6 sm:hidden">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.TITLE') }}
      </span>
      <div class="flex flex-col gap-4">
        <div v-for="(notification, index) in filteredNotificationTypes" :key="index" class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <CheckBox
              :id="`email_${notification.value}`"
              :value="`email_${notification.value}`"
              :is-checked="checkFlagStatus('email', notification.value)"
              @update="handleEmailInput"
            />
            <span class="text-body-main text-n-slate-12">
              {{ notification.label.startsWith('PROFILE_') ? $t(notification.label) : notification.label }}
            </span>
          </div>
          <span v-if="notification.description" class="text-xs text-n-slate-11 ml-6">
            {{ notification.description }}
          </span>
        </div>
      </div>

      <div class="flex items-center justify-start gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
      </div>
      <div class="flex flex-col gap-4">
        <div v-for="(notification, index) in filteredNotificationTypes" :key="index" class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <CheckBox
              :id="`push_${notification.value}`"
              :value="`push_${notification.value}`"
              :is-checked="checkFlagStatus('push', notification.value)"
              @update="handlePushInput"
            />
            <span class="text-body-main text-n-slate-12">
              {{ notification.label.startsWith('PROFILE_') ? $t(notification.label) : notification.label }}
            </span>
          </div>
          <span v-if="notification.description" class="text-xs text-n-slate-11 ml-6">
            {{ notification.description }}
          </span>
        </div>
      </div>
    </div>

    <!-- Permissão browser -->
    <div class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-n-weak rounded-2xl">
      <div class="flex flex-row items-center gap-2">
        <span
          class="i-ri-bell-line size-[18px] flex-shrink-0 text-n-slate-12"
        />
        <span class="text-body-main text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION') }}
        </span>
      </div>
      <ToggleSwitch v-model="hasEnabledPushPermissions" @change="onRequestPermissions" />
    </div>
  </div>
</template>
